import type { CrmCustomerSort, CustomerActivity, CustomerKind } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  EmptyState,
  ErrorState,
  Input,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
} from '@horevia/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { ACTIVITY_ORDER, activityTone } from '@/features/crm/labels';
import { crmCustomersQueryOptions, segmentsQueryOptions } from '@/features/crm/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort, formatNumber } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

interface CustomersSearch {
  q?: string;
  segmentId?: string;
  activity?: CustomerActivity;
  kind?: CustomerKind;
  sort?: CrmCustomerSort;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SORTS: CrmCustomerSort[] = ['spend', 'lastVisit', 'visits', 'points', 'name'];

export const Route = createFileRoute('/_app/crm/customers/')({
  validateSearch: (search: Record<string, unknown>): CustomersSearch => ({
    ...(typeof search['q'] === 'string' && search['q'] ? { q: search['q'] } : {}),
    ...(typeof search['segmentId'] === 'string' && UUID.test(search['segmentId'])
      ? { segmentId: search['segmentId'] }
      : {}),
    ...(ACTIVITY_ORDER.includes(search['activity'] as CustomerActivity)
      ? { activity: search['activity'] as CustomerActivity }
      : {}),
    ...(search['kind'] === 'PERSON' || search['kind'] === 'COMPANY'
      ? { kind: search['kind'] }
      : {}),
    ...(SORTS.includes(search['sort'] as CrmCustomerSort)
      ? { sort: search['sort'] as CrmCustomerSort }
      : {}),
  }),
  component: CrmCustomersPage,
});

/** Liste 360 des clients (ADR-025, R-CRM-02). */
function CrmCustomersPage() {
  const { t, i18n } = useTranslation(['crm', 'customers', 'common', 'errors']);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canRead = useCan('crm.read');
  const propertyId = useScopedPropertyId();
  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);
  const setSearch = (next: Partial<CustomersSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });

  const segments = useQuery(segmentsQueryOptions(canRead));
  const filters = {
    search: debounced || undefined,
    segmentId: search.segmentId,
    activity: search.activity,
    kind: search.kind,
    propertyId,
    sort: search.sort ?? 'spend',
  };
  const query = useInfiniteQuery(crmCustomersQueryOptions(filters, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const currency = 'XOF';
  const money = (value: number) => formatAmount(value, i18n.language, currency);

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('crm:customers.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('crm:customers.title')}
        subtitle={t('crm:customers.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          { label: t('crm:customers.title') },
        ]}
      />
      <Card>
        <CardBody density="flush">
          <Toolbar>
            <Input
              aria-label={t('crm:customers.search')}
              placeholder={t('crm:customers.search')}
              icon={<Search className="ico" aria-hidden="true" />}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
            <NativeSelect
              aria-label={t('crm:customers.filters.activity')}
              value={search.activity ?? ''}
              onChange={(event) =>
                setSearch({
                  activity: (event.target.value || undefined) as CustomerActivity | undefined,
                })
              }
            >
              <option value="">{t('crm:customers.filters.allActivities')}</option>
              {ACTIVITY_ORDER.map((activity) => (
                <option key={activity} value={activity}>
                  {t(`crm:activity.${activity}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('crm:customers.filters.segment')}
              value={search.segmentId ?? ''}
              onChange={(event) => setSearch({ segmentId: event.target.value || undefined })}
            >
              <option value="">{t('crm:customers.filters.allSegments')}</option>
              {(segments.data ?? []).map((segment) => (
                <option key={segment.id} value={segment.id}>
                  {segment.name}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('crm:customers.filters.kind')}
              value={search.kind ?? ''}
              onChange={(event) =>
                setSearch({ kind: (event.target.value || undefined) as CustomerKind | undefined })
              }
            >
              <option value="">{t('crm:customers.filters.allKinds')}</option>
              <option value="PERSON">{t('customers:kind.PERSON')}</option>
              <option value="COMPANY">{t('customers:kind.COMPANY')}</option>
            </NativeSelect>
            <NativeSelect
              aria-label={t('crm:customers.filters.sort')}
              value={search.sort ?? 'spend'}
              onChange={(event) => setSearch({ sort: event.target.value as CrmCustomerSort })}
            >
              {SORTS.map((sort) => (
                <option key={sort} value={sort}>
                  {t('crm:customers.filters.sort')} : {t(`crm:sort.${sort}`)}
                </option>
              ))}
            </NativeSelect>
          </Toolbar>

          {query.isError ? (
            <ErrorState
              title={t('errors:generic.title')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:trace.copy')}
              traceCopiedLabel={t('common:trace.copied')}
            />
          ) : query.isPending ? (
            <TableSkeleton rows={6} columns={6} label={t('common:states.loading')} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('crm:customers.empty')} />
          ) : (
            <TableWrap>
              <Table>
                <caption className="sr-only">{t('crm:customers.title')}</caption>
                <thead>
                  <tr>
                    <Th>{t('crm:customers.columns.customer')}</Th>
                    <Th>{t('crm:customers.columns.activity')}</Th>
                    <Th align="right">{t('crm:customers.columns.visits')}</Th>
                    <Th align="right">{t('crm:customers.columns.spend')}</Th>
                    <Th>{t('crm:customers.columns.lastVisit')}</Th>
                    <Th>{t('crm:customers.columns.loyalty')}</Th>
                    <Th>{t('crm:customers.columns.consents')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Tr key={row.id}>
                      <Td>
                        <Link
                          className="link-quiet strong"
                          to="/crm/customers/$customerId"
                          params={{ customerId: row.id }}
                        >
                          {row.displayName}
                        </Link>
                        <div className="muted t-12">
                          {t(`customers:kind.${row.kind}`)}
                          {row.phonePrimary ? ` · ${row.phonePrimary}` : ''}
                        </div>
                      </Td>
                      <Td>
                        <Badge tone={activityTone(row.activity)}>
                          {t(`crm:activity.${row.activity}`)}
                        </Badge>
                      </Td>
                      <Td align="right" numeric>
                        {formatNumber(row.visits, i18n.language)}
                        <div className="muted t-12">
                          {t('crm:customers.visitsDetail', {
                            stays: row.staysCount,
                            orders: row.ordersCount,
                          })}
                        </div>
                      </Td>
                      <Td align="right" numeric>
                        {money(row.spendTotal)}
                      </Td>
                      <Td>
                        {row.lastVisitAt ? formatDateShort(row.lastVisitAt, i18n.language) : '—'}
                      </Td>
                      <Td>
                        {row.loyalty ? (
                          <span className="t-13">
                            {row.loyalty.tierName ? (
                              <Badge tone="amber">{row.loyalty.tierName}</Badge>
                            ) : null}{' '}
                            {t('crm:pos.pointsOnly', { points: row.loyalty.balance })}
                          </span>
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td>
                        <div className="row row--wrap" style={{ gap: 4 }}>
                          {row.grantedChannels.length === 0 ? (
                            <span className="muted t-12">—</span>
                          ) : (
                            row.grantedChannels.map((channel) => (
                              <Badge key={channel} tone="green">
                                {t(`crm:consents.channels.${channel}`)}
                              </Badge>
                            ))
                          )}
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
          {query.hasNextPage ? (
            <div className="p-3">
              <Button
                variant="secondary"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('crm:customers.loadMore')}
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </Container>
  );
}
